// Normalitzador de configuracions de dashboard a la FORMA CANÒNICA.
//
// Font única de la forma canònica d'un widget. Mòdul PUR (sense dependències de Vue) perquè
// el puguin importar el runtime (getEffectiveConfig), l'editor (save/applyType) i el script
// Node de sync a BD. Contracte:
//   · pur        — no muta l'entrada; retorna còpies noves.
//   · idempotent — normalize(normalize(x)) === normalize(x).
//   · total      — accepta qualsevol widget/config legacy; no llança mai.
//   · reporting  — amb { report } recull les transformacions i els "flags" (casos que
//                  necessiten revisió humana al codemod); el runtime l'ignora.
//
// Regles canòniques (una per incoherència històrica):
//   title           → params.title multilang PLA (mai { text }); ApexCharts rep { text } al render.
//   alçades         → params.chart.{height,minHeight,maxHeight,heightPerCategory} (maxHeight entra al chart).
//   legendMaxChars  → només a nivell de pantalla; s'elimina params.legendMaxChars.
//   missingLabel    → sempre multilang.
//   formatters      → només presets string; les funcions (no serialitzables) s'eliminen + flag.
//   mapping         → només mapping.value; category/group es deriven de dimension al render → s'eliminen si coincideixen.
//   noms de camp    → dotted a levels/columns/metrics quan coincideixen (normalitzats) amb una dimensió.
//   KPI             → (de moment pass-through; la conversió a objecte discriminat és un pas posterior).

const LANGS = ['ca_es', 'es_es', 'en_gb'];
const FORMATTER_PRESETS = new Set(['thousands', 'millions', 'raw', 'percent']);

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepClone(v) {
  if (Array.isArray(v)) return v.map(deepClone);
  if (isPlainObject(v)) {
    const o = {};
    for (const k of Object.keys(v)) o[k] = deepClone(v[k]);
    return o;
  }
  return v; // primitius i funcions (es tracten a part i s'eliminen)
}

/** Clau de comparació de noms de camp/dimensió: mateixa normalització que resolveProp. */
function normKey(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[\[\]"= _.]/g, '');
}

/** Converteix un valor de títol/etiqueta a multilang pla. */
function toMultilang(v) {
  if (v == null) return v;
  if (typeof v === 'string') {
    const o = {};
    for (const l of LANGS) o[l] = v;
    return o;
  }
  if (isPlainObject(v)) return { ...v };
  return v;
}

/** El multilang pla a partir d'un títol en forma { text } o directe. */
function flattenTitle(title) {
  if (isPlainObject(title) && title.text !== undefined) return toMultilang(title.text);
  return toMultilang(title);
}

/** Dimensions com a array net de buits. */
function dimsOf(params) {
  const d = params?.dimension;
  if (Array.isArray(d)) return d.filter(Boolean);
  return d ? [d] : [];
}

/**
 * Normalitza un widget a la forma canònica. No muta l'entrada.
 * @param {object} widget
 * @param {{changes?: string[], flags?: string[]}} [report]
 * @return {object} widget canònic
 */
export function normalizeWidget(widget, report) {
  if (!isPlainObject(widget)) return widget;
  const log = (msg) => { if (report) (report.changes || (report.changes = [])).push(`${widget.id || '?'}: ${msg}`); };
  const flag = (msg) => { if (report) (report.flags || (report.flags = [])).push(`${widget.id || '?'}: ${msg}`); };

  const w = deepClone(widget);
  if (!isPlainObject(w.params)) w.params = {};
  const p = w.params;

  // active per defecte true
  if (w.active === undefined) w.active = true;

  // --- Títol → multilang pla ------------------------------------------------
  if (p.title !== undefined) {
    const before = p.title;
    p.title = flattenTitle(p.title);
    if (isPlainObject(before) && before.text !== undefined) log('title: {text} → pla');
    else if (typeof before === 'string') log('title: string → multilang');
  }

  // --- Alçades → params.chart.* --------------------------------------------
  if (p.maxHeight !== undefined) {
    if (!isPlainObject(p.chart)) p.chart = p.chart ? { type: p.chart } : {};
    if (p.chart.maxHeight === undefined) p.chart.maxHeight = p.maxHeight;
    delete p.maxHeight;
    log('maxHeight → chart.maxHeight');
  }

  // --- legendMaxChars de widget → fora (es queda el de pantalla) ------------
  if (p.legendMaxChars !== undefined) {
    delete p.legendMaxChars;
    log('legendMaxChars de widget eliminat (es manté el de pantalla)');
  }

  // --- missingLabel → multilang --------------------------------------------
  if (typeof p.missingLabel === 'string') {
    p.missingLabel = toMultilang(p.missingLabel);
    log('missingLabel: string → multilang');
  }

  // --- Formatters → només presets string -----------------------------------
  const fixFormatter = (host, where) => {
    if (!isPlainObject(host) || host.formatter === undefined) return;
    const f = host.formatter;
    if (typeof f === 'function') { delete host.formatter; flag(`formatter funció eliminada a ${where} (no serialitzable)`); return; }
    if (typeof f === 'string' && f !== '' && !FORMATTER_PRESETS.has(f)) { delete host.formatter; flag(`formatter desconegut "${f}" eliminat a ${where}`); }
  };
  fixFormatter(p.dataLabels, 'dataLabels');
  fixFormatter(p.xaxis?.labels, 'xaxis.labels');
  fixFormatter(p.yaxis?.labels, 'yaxis.labels');
  fixFormatter(p.tooltip?.y, 'tooltip.y');

  // --- mapping: només value; category/group derivats de dimension ----------
  if (isPlainObject(p.mapping)) {
    const dims = dimsOf(p);
    if (p.mapping.value === undefined) p.mapping.value = 'count';
    const cat = p.mapping.category;
    if (cat !== undefined) {
      if (dims.length && normKey(cat) === normKey(dims[0])) { delete p.mapping.category; log('mapping.category == dimension → eliminat'); }
      else if (cat === '' ) { delete p.mapping.category; }
      else if (dims.length) { flag(`mapping.category "${cat}" ≠ dimension[0] "${dims[0]}" (es manté)`); }
    }
    const grp = p.mapping.group;
    if (grp !== undefined) {
      if (dims.length > 1 && normKey(grp) === normKey(dims[1])) { delete p.mapping.group; log('mapping.group == dimension[1] → eliminat'); }
      else if (grp === '') { delete p.mapping.group; }
      else if (dims.length > 1) { flag(`mapping.group "${grp}" ≠ dimension[1] "${dims[1]}" (es manté)`); }
    }
  }

  // --- map/world-map: dataKeyField/valueField → deriven de dimension/mapping -
  if (w.type === 'map' || w.type === 'world-map') {
    const dims = dimsOf(p);
    if (p.dataKeyField !== undefined && dims.length && normKey(p.dataKeyField) === normKey(dims[0])) {
      delete p.dataKeyField; log('dataKeyField == dimension → eliminat');
    }
    if (p.valueField !== undefined) {
      const val = isPlainObject(p.mapping) ? p.mapping.value : undefined;
      if (normKey(p.valueField) === normKey(val || 'count')) { delete p.valueField; log('valueField == mapping.value → eliminat'); }
    }
  }

  // --- Noms de camp (levels/columns/metrics): NOMÉS es marquen (flag) ---------
  // No es reescriuen en runtime: les taules/mètriques/arbre resolen el camp per
  // coincidència EXACTA (getTableCellValue / clau "FUNC(field)"), no per resolveProp,
  // de manera que canviar underscore→dotted trencaria la cerca. La unificació dotted
  // als FITXERS es fa al codemod (workstream F) amb verificació, no aquí.
  if (report) {
    const dimList = dimsOf(p);
    const flagField = (field, where) => {
      if (typeof field !== 'string' || !field.includes('_')) return;
      const hit = dimList.find(d => d !== field && normKey(d) === normKey(field));
      if (hit) flag(`camp "${field}" a ${where} podria ser dotted "${hit}" (revisar al codemod)`);
    };
    if (Array.isArray(p.levels)) p.levels.forEach(l => { if (isPlainObject(l)) flagField(l.field, 'levels'); });
    if (Array.isArray(p.columns)) p.columns.forEach(c => { if (isPlainObject(c)) flagField(c.field, 'columns'); });
    if (Array.isArray(p.metrics)) p.metrics.forEach(m => { if (isPlainObject(m)) flagField(m.field, 'metrics'); });
  }

  return w;
}

/**
 * Normalitza una pantalla (widgets + claus de pantalla preservades).
 * @param {object} screen
 * @param {object} [report]
 */
export function normalizeScreen(screen, report) {
  if (!isPlainObject(screen)) return screen;
  const s = { ...screen };
  if (Array.isArray(screen.widgets)) s.widgets = screen.widgets.map(w => normalizeWidget(w, report));
  return s;
}

/**
 * Normalitza una llista de pantalles (config completa).
 * @param {Array} screens
 * @param {object} [report] recull { changes, flags }
 * @return {Array} pantalles canòniques
 */
export function normalizeConfig(screens, report) {
  if (!Array.isArray(screens)) return screens;
  return screens.map(s => normalizeScreen(s, report));
}
